import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import components
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import Login from './components/auth/Login';
import ProductList from './components/products/ProductList';
import ProductForm from './components/products/ProductForm';
import LowStock from './components/products/LowStock';
import CategoryList from './components/categories/CategoryList';
import OrderList from './components/orders/OrderList';
import OrderForm from './components/orders/OrderForm';
import CustomerList from './components/customers/CustomerList';
import CustomerForm from './components/customers/CustomerForm';
import CustomerDetail from './components/customers/CustomerDetail';
import InvoiceList from './components/invoices/InvoiceList';
import InvoiceForm from './components/invoices/InvoiceForm';
import InvoiceDetail from './components/invoices/InvoiceDetail';
import PaymentList from './components/payments/PaymentList';
import PaymentForm from './components/payments/PaymentForm';
import CustomerBalance from './components/payments/CustomerBalance';
import CustomerBalanceList from './components/payments/CustomerBalanceList';
import StatementView from './components/statements/StatementView';
import Settings from './components/settings/Settings';
import ShipmentList from './components/shipments/ShipmentList';
import ShipmentForm from './components/shipments/ShipmentForm';
import ShipmentDetail from './components/shipments/ShipmentDetail';
import SupplierList from './components/suppliers/SupplierList';
import SupplierForm from './components/suppliers/SupplierForm';
import SupplierOrderList from './components/supplier-orders/SupplierOrderList';
import SupplierOrderForm from './components/supplier-orders/SupplierOrderForm';
import SupplierOrderDetail from './components/supplier-orders/SupplierOrderDetail';
import ProtectedComponent from './components/auth/ProtectedComponent';

// Import context
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { AuthorizationProvider } from './context/AuthorizationContext';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.2rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.3rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.1rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

// Component to handle dashboard access or redirect to user's main page
const DashboardOrRedirect: React.FC = () => {
  const { hasPermission } = useAuth();

  // If user has dashboard access, show dashboard
  if (hasPermission('dashboard.read')) {
    return <Dashboard />;
  }

  // Redirect based on user's role/permissions
  if (hasPermission('shipments.read')) {
    return <Navigate to="/shipments" replace />;
  } else if (hasPermission('orders.read')) {
    return <Navigate to="/orders" replace />;
  } else if (hasPermission('categories.read')) {
    return <Navigate to="/categories" replace />;
  } else {
    // Fallback to settings page which is always accessible
    return <Navigate to="/settings" replace />;
  }
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AuthorizationProvider>
        <SettingsProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ToastContainer position="top-right" autoClose={5000} />
            <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardOrRedirect />} />
              
              {/* Product Routes */}
              <Route path="/products" element={<ProductList />} />
              <Route path="/products/add" element={<ProductForm />} />
              <Route path="/products/edit/:id" element={<ProductForm />} />
              <Route path="/products/low-stock" element={<LowStock />} />
              
              {/* Category Routes */}
              <Route path="/categories" element={
                <ProtectedComponent requiredPermission="categories.create" showFallback fallback={<Navigate to="/orders" replace />}>
                  <CategoryList />
                </ProtectedComponent>
              } />
              
              {/* Order Routes */}
              <Route path="/orders" element={<OrderList />} />
              <Route path="/orders/add" element={<OrderForm />} />
              <Route path="/orders/edit/:id" element={<OrderForm />} />
              <Route path="/orders/:id" element={<OrderForm />} />
              
              {/* Customer Routes */}
              <Route path="/customers" element={<CustomerList />} />
              <Route path="/customers/add" element={<CustomerForm />} />
              <Route path="/customers/edit/:id" element={<CustomerForm />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              
              {/* Invoice Routes */}
              <Route path="/invoices" element={<InvoiceList />} />
              <Route path="/invoices/add" element={<InvoiceForm />} />
              <Route path="/invoices/edit/:id" element={<InvoiceForm />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              
              {/* Payment Routes */}
              <Route path="/payments" element={<PaymentList />} />
              <Route path="/payments/new" element={<PaymentForm />} />
              <Route path="/payments/:id" element={<PaymentForm />} />
              <Route path="/payments/:id/edit" element={<PaymentForm />} />
              <Route path="/customer-balances" element={<CustomerBalanceList />} />
              <Route path="/statements" element={<StatementView />} />
              <Route path="/customers/:customerId/balance" element={<CustomerBalance />} />
              
              {/* Shipment Routes */}
              <Route path="/shipments" element={<ShipmentList />} />
              <Route path="/shipments/new" element={<ShipmentForm />} />
              <Route path="/shipments/:id" element={<ShipmentDetail />} />
              <Route path="/shipments/:id/edit" element={<ShipmentForm />} />
              <Route path="/shipments/:id/receive" element={<ShipmentDetail />} />

              {/* Supplier Routes */}
              <Route path="/suppliers" element={<SupplierList />} />
              <Route path="/suppliers/add" element={<SupplierForm />} />
              <Route path="/suppliers/edit/:id" element={<SupplierForm />} />

              {/* Supplier Order Routes */}
              <Route path="/supplier-orders" element={<SupplierOrderList />} />
              <Route path="/supplier-orders/new" element={<SupplierOrderForm />} />
              <Route path="/supplier-orders/:id" element={<SupplierOrderDetail />} />
              <Route path="/supplier-orders/:id/edit" element={<SupplierOrderForm />} />

              {/* Settings Route */}
              <Route path="/settings" element={<Settings />} />
            </Route>
          </Routes>
          </Router>
        </ThemeProvider>
        </SettingsProvider>
      </AuthorizationProvider>
    </AuthProvider>
  );
};

export default App;
